/**
 * ProductId Value Object
 * Ensures product identifiers are valid and provides type safety
 */
export class ProductId {
  private static readonly PRODUCT_ID_PREFIX = 'prod_';
  private static readonly MINIMUM_ID_LENGTH = 3;

  private constructor(private readonly uniqueIdentifier: string) {}

  static fromExistingId(existingProductId: string): ProductId {
    const normalizedProductId = this.validateAndNormalizeProductId(existingProductId);
    return new ProductId(normalizedProductId);
  }

  static generateNewProductId(): ProductId {
    const timestampComponent = this.createTimestampComponent();
    const randomComponent = this.createRandomComponent();
    const generatedProductId = `${ProductId.PRODUCT_ID_PREFIX}${timestampComponent}_${randomComponent}`;

    return new ProductId(generatedProductId);
  }

  getUniqueIdentifier(): string {
    return this.uniqueIdentifier;
  }

  isEqualTo(otherProductId: ProductId): boolean {
    return this.uniqueIdentifier === otherProductId.uniqueIdentifier;
  }

  formatForDisplay(): string {
    return this.uniqueIdentifier.toUpperCase();
  }

  private static validateAndNormalizeProductId(productId: string): string {
    const normalizedProductId = productId?.trim();

    if (!normalizedProductId) {
      throw new Error('Product ID cannot be empty or null');
    }

    if (normalizedProductId.length < ProductId.MINIMUM_ID_LENGTH) {
      throw new Error(`Product ID must be at least ${ProductId.MINIMUM_ID_LENGTH} characters long`);
    }

    return normalizedProductId;
  }

  private static createTimestampComponent(): string {
    return Date.now().toString(36);
  }

  private static createRandomComponent(): string {
    return Math.random().toString(36).substring(2, 8);
  }
}
